import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Button, Loader } from "@components/atoms";
import { CheckoutProgressBar } from "@components/molecules";
import { CartSummary } from "@components/organisms";
import { Checkout } from "@components/templates";
import usePrevious from "@react-hook/previous";
import { useCart, useCheckout } from "@sdk/react";
import { NoteFields } from "@temp/@next/components/organisms/NotesForm/types";
import { usePreferences } from "@temp/@next/hooks";
import { generateProductUrlWithAttributes } from "@temp/components/ProductDetails/utils";
import { CHECKOUT_STEPS } from "@temp/core/config";
import React, { useEffect, useRef, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { RudderAnalytics } from "../../../app/analitics";
import { OverlayType } from "../../../components/Overlay/context";
import { CheckoutRouter } from "./CheckoutRouter";
import { CheckoutAddressSubpage, CheckoutBillingAddressSubpage, CheckoutReviewSubpage, CheckoutShippingSubpage } from "./subpages";
import { CheckoutNotesSubpage } from "./subpages/CheckoutNotesSubpage";
// eslint-disable-next-line prettier/prettier
var CheckoutPaymentSubpage = React.lazy(function () {
  return import(
  /* webpackChunkName: "checkout-payment-subpage" */
  "./subpages/CheckoutPaymentSubpage");
});

var prepareCartSummary = function prepareCartSummary(totalPrice, subtotalPrice, shippingTaxedPrice, promoTaxedPrice, items) {
  var products = items === null || items === void 0 ? void 0 : items.map(function (_ref) {
    var _variant$product, _variant$product$thum, _variant$product2, _variant$product2$thu, _variant$product3, _variant$product3$thu;

    var id = _ref.id,
        variant = _ref.variant,
        productTotalPrice = _ref.totalPrice,
        quantity = _ref.quantity;
    return {
      id: id || "",
      name: variant.name || "",
      price: {
        gross: {
          amount: (productTotalPrice === null || productTotalPrice === void 0 ? void 0 : productTotalPrice.gross.amount) || 0,
          currency: (productTotalPrice === null || productTotalPrice === void 0 ? void 0 : productTotalPrice.gross.currency) || ""
        },
        net: {
          amount: (productTotalPrice === null || productTotalPrice === void 0 ? void 0 : productTotalPrice.net.amount) || 0,
          currency: (productTotalPrice === null || productTotalPrice === void 0 ? void 0 : productTotalPrice.net.currency) || ""
        }
      },
      quantity: quantity,
      sku: variant.sku || "",
      thumbnail: {
        alt: ((_variant$product = variant.product) === null || _variant$product === void 0 ? void 0 : (_variant$product$thum = _variant$product.thumbnail) === null || _variant$product$thum === void 0 ? void 0 : _variant$product$thum.alt) || undefined,
        url: (_variant$product2 = variant.product) === null || _variant$product2 === void 0 ? void 0 : (_variant$product2$thu = _variant$product2.thumbnail) === null || _variant$product2$thu === void 0 ? void 0 : _variant$product2$thu.url,
        url2x: (_variant$product3 = variant.product) === null || _variant$product3 === void 0 ? void 0 : (_variant$product3$thu = _variant$product3.thumbnail2x) === null || _variant$product3$thu === void 0 ? void 0 : _variant$product3$thu.url
      },
      title: (variant.product || {}).name
    };
  });
  return function RenderCartSummary(mobileCartOpened, setMobileCartOpened, overlayContext) {
    return React.createElement(CartSummary, {
      shipping: shippingTaxedPrice,
      subtotal: subtotalPrice,
      promoCode: promoTaxedPrice,
      total: totalPrice,
      products: products,
      mobileCartOpened: mobileCartOpened,
      items: items,
      toggleMobileCart: function toggleMobileCart() {
        if (mobileCartOpened) {
          overlayContext.hide();
          setMobileCartOpened(false);
        } else {
          overlayContext.show(OverlayType.checkout); // just to add className hidden to <body></body>

          setMobileCartOpened(true);
        }
      }
    });
  };
};

var getCheckoutProgress = function getCheckoutProgress(loaded, activeStepIndex, isShippingRequired) {
  var steps = isShippingRequired ? CHECKOUT_STEPS : CHECKOUT_STEPS.filter(function (_ref2) {
    var onlyIfShippingRequired = _ref2.onlyIfShippingRequired;
    return !onlyIfShippingRequired;
  });
  return loaded ? React.createElement(CheckoutProgressBar, {
    steps: steps,
    activeStep: activeStepIndex
  }) : null;
};

var getButton = function getButton(text, onClick) {
  if (text) {
    return React.createElement(Button, {
      "data-cy": "checkoutPageBtnNextStep",
      onClick: onClick,
      type: "submit"
    }, text);
  }

  return null;
};

var CheckoutPage = function CheckoutPage(props) {
  var _checkout$shippingMet;

  var history = useHistory();

  var _useLocation = useLocation(),
      pathname = _useLocation.pathname;

  var _useCart = useCart(),
      cartLoaded = _useCart.loaded,
      shippingPrice = _useCart.shippingPrice,
      discount = _useCart.discount,
      subtotalPrice = _useCart.subtotalPrice,
      totalPrice = _useCart.totalPrice,
      items = _useCart.items,
      cartPromoCode = _useCart.cartPromoCode,
      setCartPromoCode = _useCart.setCartPromoCode;

  var _useCheckout = useCheckout(),
      checkoutLoaded = _useCheckout.loaded,
      checkout = _useCheckout.checkout,
      payment = _useCheckout.payment,
      addPromoCode = _useCheckout.addPromoCode,
      promoCodeDiscount = _useCheckout.promoCodeDiscount;

  var _usePreferences = usePreferences(),
      locale = _usePreferences.preferences.locale;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      submitInProgress = _useState2[0],
      setSubmitInProgress = _useState2[1];

  var _useState3 = useState(payment === null || payment === void 0 ? void 0 : payment.gateway),
      _useState4 = _slicedToArray(_useState3, 2),
      selectedPaymentGateway = _useState4[0],
      setSelectedPaymentGateway = _useState4[1];

  var _useState5 = useState(payment === null || payment === void 0 ? void 0 : payment.token),
      _useState6 = _slicedToArray(_useState5, 2),
      selectedPaymentGatewayToken = _useState6[0],
      setSelectedPaymentGatewayToken = _useState6[1];

  useEffect(function () {
    setSelectedPaymentGateway(payment === null || payment === void 0 ? void 0 : payment.gateway);
  }, [payment === null || payment === void 0 ? void 0 : payment.gateway]);
  useEffect(function () {
    setSelectedPaymentGatewayToken(payment === null || payment === void 0 ? void 0 : payment.token);
  }, [payment === null || payment === void 0 ? void 0 : payment.token]);
  var previousCheckoutId = usePrevious(checkout === null || checkout === void 0 ? void 0 : checkout.id);
  useEffect(function () {
    // switching to checkout promo code from when checkout is created
    var switchToCheckoutPromoCode = function switchToCheckoutPromoCode() {
      if ((cartPromoCode === null || cartPromoCode === void 0 ? void 0 : cartPromoCode.voucherCode) && (checkout === null || checkout === void 0 ? void 0 : checkout.id) && !(promoCodeDiscount === null || promoCodeDiscount === void 0 ? void 0 : promoCodeDiscount.voucherCode)) {
        addPromoCode(cartPromoCode === null || cartPromoCode === void 0 ? void 0 : cartPromoCode.voucherCode);
        setCartPromoCode(null);
      }
    };

    var checkoutIdArrived = previousCheckoutId !== (checkout === null || checkout === void 0 ? void 0 : checkout.id);

    if (checkoutIdArrived) {
      switchToCheckoutPromoCode();
    }
  }, [addPromoCode, cartPromoCode === null || cartPromoCode === void 0 ? void 0 : cartPromoCode.voucherCode, checkout === null || checkout === void 0 ? void 0 : checkout.id, previousCheckoutId, promoCodeDiscount === null || promoCodeDiscount === void 0 ? void 0 : promoCodeDiscount.voucherCode, setCartPromoCode]);
  var matchingStepIndex = CHECKOUT_STEPS.findIndex(function (_ref3) {
    var link = _ref3.link;
    return link === pathname;
  });
  var activeStepIndex = matchingStepIndex !== -1 ? matchingStepIndex : 2;
  var activeStep = CHECKOUT_STEPS[activeStepIndex];
  useEffect(function () {
    // @ analytics
    if (matchingStepIndex !== -1) {
      var _products = items === null || items === void 0 ? void 0 : items.map(function (z) {
        var _z$variant$product, _z$variant$product2, _z$variant$product3, _z$totalPrice;

        return {
          category: (_z$variant$product = z.variant.product) === null || _z$variant$product === void 0 ? void 0 : _z$variant$product.category.name,
          id: (_z$variant$product2 = z.variant.product) === null || _z$variant$product2 === void 0 ? void 0 : _z$variant$product2.id,
          name: (_z$variant$product3 = z.variant.product) === null || _z$variant$product3 === void 0 ? void 0 : _z$variant$product3.name,
          price: (_z$totalPrice = z.totalPrice) === null || _z$totalPrice === void 0 ? void 0 : _z$totalPrice.gross.amount,
          quantity: z.quantity,
          variant: z.variant.id
        };
      });

      if (matchingStepIndex === 0) {
        // TagManager.dataLayer({
        //   dataLayer: {
        //     ecommerce: {
        //       checkout: {
        //         products: _products,
        //         step: "InitiateCheckout",
        //       },
        //     },
        //     event: "checkout",
        //   },
        // });
        RudderAnalytics.track("Checkout Started", {
          currency: totalPrice === null || totalPrice === void 0 ? void 0 : totalPrice.gross.currency,
          products: _products,
          value: totalPrice === null || totalPrice === void 0 ? void 0 : totalPrice.gross.amount
        });
      } else if (matchingStepIndex === 1) {
        // TagManager.dataLayer({
        //   dataLayer: {
        //     ecommerce: {
        //       checkout: {
        //         products: _products,
        //         step: "AddAddressInfo",
        //       },
        //     },
        //     event: "checkout",
        //   },
        // });
        RudderAnalytics.track("Checkout Step Viewed", {
          checkout_id: checkout === null || checkout === void 0 ? void 0 : checkout.id,
          step: 1 // }, {
          //   context: {
          //     traits: {
          //       plan: "Paid, Premium"
          //     }
          //   }

        });
      } else if (matchingStepIndex === 2) {
        // TagManager.dataLayer({
        //   dataLayer: {
        //     ecommerce: {
        //       checkout: {
        //         products: _products,
        //         step: "AddPaymentInfo",
        //       },
        //     },
        //     event: "checkout",
        //   },
        // });
        RudderAnalytics.track("Payment Info Entered", {
          checkout_id: checkout === null || checkout === void 0 ? void 0 : checkout.id,
          step: 2 // }, {
          //   context: {
          //     traits: {
          //       plan: "Paid, Premium"
          //     }
          //   }

        });
      }
    }
  }, [matchingStepIndex]);
  var checkoutAddressSubpageRef = useRef(null);
  var checkoutBillingAddressSubpageRef = useRef(null);
  var checkoutNotesAddressSubpageRef = useRef(null);
  var checkoutNotesShippingSubpageRef = useRef(null);
  var checkoutShippingSubpageRef = useRef(null);
  var checkoutPaymentSubpageRef = useRef(null);
  var checkoutReviewSubpageRef = useRef(null);

  if (cartLoaded && (!items || !(items === null || items === void 0 ? void 0 : items.length))) {
    return React.createElement(Redirect, {
      to: "/cart/"
    });
  }

  var handleCheckoutAddressSuccess = function handleCheckoutAddressSuccess() {
    var _checkoutBillingAddre;

    if ((_checkoutBillingAddre = checkoutBillingAddressSubpageRef.current) === null || _checkoutBillingAddre === void 0 ? void 0 : _checkoutBillingAddre.submitBillingAddress) {
      checkoutBillingAddressSubpageRef.current.submitBillingAddress();
    }
  };

  var handleCheckoutBillingAddressSuccess = function handleCheckoutBillingAddressSuccess() {
    var _checkoutNotesAddress;

    if ((_checkoutNotesAddress = checkoutNotesAddressSubpageRef.current) === null || _checkoutNotesAddress === void 0 ? void 0 : _checkoutNotesAddress.submitNotes) {
      checkoutNotesAddressSubpageRef.current.submitNotes();
    }
  };

  var handleCheckoutShippingSuccess = function handleCheckoutShippingSuccess() {
    var _checkoutNotesShippin;

    if ((_checkoutNotesShippin = checkoutNotesShippingSubpageRef.current) === null || _checkoutNotesShippin === void 0 ? void 0 : _checkoutNotesShippin.submitNotes) {
      checkoutNotesShippingSubpageRef.current.submitNotes();
    }
  };

  var handleCheckoutPaymentSuccess = function handleCheckoutPaymentSuccess() {
    var _checkoutReviewSubpag;

    if ((_checkoutReviewSubpag = checkoutReviewSubpageRef.current) === null || _checkoutReviewSubpag === void 0 ? void 0 : _checkoutReviewSubpag.complete) {
      checkoutReviewSubpageRef.current.complete();
    }
  };

  var handleCheckoutReviewSuccess = function handleCheckoutReviewSuccess(data) {
    history.push({
      pathname: CHECKOUT_STEPS[2].nextStepLink,
      state: {
        id: data === null || data === void 0 ? void 0 : data.id,
        items: items === null || items === void 0 ? void 0 : items.map(function (item) {
          var _item$variant$product, _item$variant$product2, _item$variant$product3, _item$variant$product4, _item$productAttribut;

          return {
            categoryName: (_item$variant$product = item.variant.product) === null || _item$variant$product === void 0 ? void 0 : (_item$variant$product2 = _item$variant$product.category) === null || _item$variant$product2 === void 0 ? void 0 : _item$variant$product2.name,
            imageUrl: (_item$variant$product3 = item.variant.product) === null || _item$variant$product3 === void 0 ? void 0 : (_item$variant$product4 = _item$variant$product3.thumbnail2x) === null || _item$variant$product4 === void 0 ? void 0 : _item$variant$product4.url,
            key: item.variant.id + ((_item$productAttribut = item.productAttributes) === null || _item$productAttribut === void 0 ? void 0 : _item$productAttribut.map(function (attr) {
              return attr.attributeId + attr.values.join();
            }).join())
          };
        }),
        orderNumber: data === null || data === void 0 ? void 0 : data.number,
        shareUrl: getShareUrl(),
        token: data === null || data === void 0 ? void 0 : data.token
      }
    });
  };

  var handleNextStepClick = function handleNextStepClick() {
    var _checkoutAddressSubpa, _checkoutShippingSubp, _checkoutPaymentSubpa, _checkoutReviewSubpag2;

    switch (activeStepIndex) {
      case 0:
        if ((_checkoutAddressSubpa = checkoutAddressSubpageRef.current) === null || _checkoutAddressSubpa === void 0 ? void 0 : _checkoutAddressSubpa.submitAddress) {
          checkoutAddressSubpageRef.current.submitAddress();
        }

        break;

      case 1:
        if ((_checkoutShippingSubp = checkoutShippingSubpageRef.current) === null || _checkoutShippingSubp === void 0 ? void 0 : _checkoutShippingSubp.submitShipping) {
          checkoutShippingSubpageRef.current.submitShipping();
        }

        break;

      case 2:
        if ((_checkoutPaymentSubpa = checkoutPaymentSubpageRef.current) === null || _checkoutPaymentSubpa === void 0 ? void 0 : _checkoutPaymentSubpa.submitPayment) {
          checkoutPaymentSubpageRef.current.submitPayment(); // @ analytics
          // TagManager.dataLayer({
          //   dataLayer: {
          //     ecommerce: {
          //       purchase: {
          //         products: items?.map(z => ({
          //           currency: z.totalPrice?.gross.currency,
          //           id: z.variant.id,
          //           name: z.variant.product?.name,
          //           price: z.totalPrice?.gross.amount,
          //           quantity: z.quantity,
          //         })),
          //       },
          //     },
          //     event: "purchase",
          //   },
          // });

          RudderAnalytics.track("Order Completed", {
            checkout_id: checkout === null || checkout === void 0 ? void 0 : checkout.id,
            currency: totalPrice === null || totalPrice === void 0 ? void 0 : totalPrice.gross.currency,
            products: items === null || items === void 0 ? void 0 : items.map(function (z) {
              var _z$totalPrice2, _z$variant$product4, _z$totalPrice3;

              return {
                currency: (_z$totalPrice2 = z.totalPrice) === null || _z$totalPrice2 === void 0 ? void 0 : _z$totalPrice2.gross.currency,
                id: z.variant.id,
                name: (_z$variant$product4 = z.variant.product) === null || _z$variant$product4 === void 0 ? void 0 : _z$variant$product4.name,
                price: (_z$totalPrice3 = z.totalPrice) === null || _z$totalPrice3 === void 0 ? void 0 : _z$totalPrice3.gross.amount,
                quantity: z.quantity
              };
            }),
            total: totalPrice === null || totalPrice === void 0 ? void 0 : totalPrice.gross.amount
          }, null, null);
        }

        break;

      case 3:
        if ((_checkoutReviewSubpag2 = checkoutReviewSubpageRef.current) === null || _checkoutReviewSubpag2 === void 0 ? void 0 : _checkoutReviewSubpag2.complete) {
          checkoutReviewSubpageRef.current.complete(); // @ analytics
          // RudderAnalytics.track("Order Completed" , {
          //   checkout_id: checkout?.id,
          //   currency: totalPrice?.gross.currency,
          //   products: items?.map(z => ({
          //     category: z.variant.product?.category.name,
          //     id: z.variant.product?.id,
          //     name: z.variant.product?.name,
          //     price: z.totalPrice?.gross.amount,
          //     quantity: z.quantity,
          //     variant: z.variant.id,
          //   })),
          //   total: totalPrice?.gross.amount,
          // }, {
          //   context: {
          //     traits: {
          //       plan: "Paid, Premium"
          //     }
          //   }
          // }, null);
        }

        break;

      default:
        break;
    }
  };

  var shippingTaxedPrice = (checkout === null || checkout === void 0 ? void 0 : (_checkout$shippingMet = checkout.shippingMethod) === null || _checkout$shippingMet === void 0 ? void 0 : _checkout$shippingMet.id) && shippingPrice ? {
    gross: shippingPrice,
    net: shippingPrice
  } : null;
  var promoTaxedPrice = discount && {
    gross: discount,
    net: discount
  };

  var getShareUrl = function getShareUrl() {
    var _ref4 = items || [],
        _ref5 = _slicedToArray(_ref4, 1),
        firstItem = _ref5[0];

    if (!firstItem) {
      return;
    }

    var _firstItem$productAtt = firstItem.productAttributes,
        productAttributes = _firstItem$productAtt === void 0 ? [] : _firstItem$productAtt,
        _firstItem$variant = firstItem.variant,
        _firstItem$variant$at = _firstItem$variant.attributes,
        attributes = _firstItem$variant$at === void 0 ? [] : _firstItem$variant$at,
        _firstItem$variant$pr = _firstItem$variant.product;
    _firstItem$variant$pr = _firstItem$variant$pr === void 0 ? {} : _firstItem$variant$pr;
    var name = _firstItem$variant$pr.name,
        id = _firstItem$variant$pr.id;

    if (!name || !id) {
      return;
    }

    var allAttributes = [].concat(_toConsumableArray(productAttributes), _toConsumableArray(attributes)).reduce(function (acc, cur) {
      var _cur$values = _slicedToArray(cur.values, 1),
          value = _cur$values[0];

      var key = "attribute" in cur ? cur.attribute.name : undefined;

      if (!key) {
        return acc;
      }

      if (typeof value === "string") {
        acc[key] = value;
        return acc;
      }

      if (typeof (value === null || value === void 0 ? void 0 : value.value) === "string") {
        acc[key] = value === null || value === void 0 ? void 0 : value.value;
      }

      return acc;
    }, {});
    var shareUrl = generateProductUrlWithAttributes({
      name: name,
      id: id
    }, allAttributes);
    return "".concat(window.location.origin).concat(shareUrl);
  };

  var checkoutView = cartLoaded && checkoutLoaded ? React.createElement(CheckoutRouter, {
    items: items,
    checkout: checkout,
    payment: payment,
    renderAddress: function renderAddress(renderProps) {
      return React.createElement(React.Fragment, null, React.createElement(CheckoutAddressSubpage, _extends({
        ref: checkoutAddressSubpageRef,
        changeSubmitProgress: setSubmitInProgress,
        onSuccess: handleCheckoutAddressSuccess
      }, renderProps)), React.createElement(CheckoutBillingAddressSubpage, _extends({
        ref: checkoutBillingAddressSubpageRef,
        changeSubmitProgress: setSubmitInProgress,
        onSuccess: handleCheckoutBillingAddressSuccess
      }, renderProps)), React.createElement(CheckoutNotesSubpage, _extends({
        noteFields: [NoteFields.note],
        ref: checkoutNotesAddressSubpageRef,
        changeSubmitProgress: setSubmitInProgress,
        onSuccess: function onSuccess() {
          return history.push(CHECKOUT_STEPS[0].nextStepLink);
        }
      }, renderProps)));
    },
    renderShipping: function renderShipping(renderProps) {
      return React.createElement(React.Fragment, null, React.createElement(CheckoutShippingSubpage, _extends({
        ref: checkoutShippingSubpageRef,
        changeSubmitProgress: setSubmitInProgress,
        onSuccess: handleCheckoutShippingSuccess
      }, renderProps)), React.createElement(CheckoutNotesSubpage, _extends({
        noteFields: [NoteFields.deliveryNote],
        ref: checkoutNotesShippingSubpageRef,
        changeSubmitProgress: setSubmitInProgress,
        onSuccess: function onSuccess() {
          return history.push(CHECKOUT_STEPS[1].nextStepLink);
        }
      }, renderProps)));
    },
    renderPayment: function renderPayment(renderProps) {
      return React.createElement(React.Fragment, null, React.createElement(CheckoutReviewSubpage, _extends({
        ref: checkoutReviewSubpageRef,
        selectedPaymentGatewayToken: selectedPaymentGatewayToken,
        changeSubmitProgress: setSubmitInProgress,
        onSuccess: handleCheckoutReviewSuccess
      }, renderProps)), React.createElement(React.Suspense, {
        fallback: React.createElement(Loader, null)
      }, React.createElement(CheckoutPaymentSubpage, _extends({
        ref: checkoutPaymentSubpageRef,
        selectedPaymentGateway: selectedPaymentGateway,
        selectedPaymentGatewayToken: selectedPaymentGatewayToken,
        changeSubmitProgress: setSubmitInProgress,
        selectPaymentGateway: setSelectedPaymentGateway,
        onSuccess: handleCheckoutPaymentSuccess
      }, renderProps))));
    }
  }) : React.createElement(Loader, null);
  var isShippingRequiredForProducts = items && items.some(function (_ref6) {
    var _variant$product4;

    var variant = _ref6.variant;
    return (_variant$product4 = variant.product) === null || _variant$product4 === void 0 ? void 0 : _variant$product4.productType.isShippingRequired;
  });
  return React.createElement(Checkout, {
    loading: submitInProgress,
    navigation: getCheckoutProgress(cartLoaded && checkoutLoaded, activeStepIndex, !!isShippingRequiredForProducts),
    cartSummary: prepareCartSummary(totalPrice, subtotalPrice, shippingTaxedPrice, promoTaxedPrice, items || undefined // TODO: consider to use IItems hereinafter
    ),
    checkout: checkoutView,
    button: getButton(activeStep.nextActionName[locale].toUpperCase(), handleNextStepClick)
  });
};

export { CheckoutPage };